import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { formatDate } from '@klp/utils/dateTimeFormatter';
import { Box, Grid, makeStyles } from '@material-ui/core';
import {
    Heading,
    Block,
    ActionButton,
    Typography,
    Tooltip,
    Loader,
    Amount,
} from '@klp/design';
import { showToast } from '@klpkonsern/ui';

import { KlpPalette } from '@klp/design/Theme/KlpPalette';

import type { MUIDataTableColumn, MUIDataTableOptions } from 'mui-datatables';
import MUIDataTable from 'mui-datatables';
import { theme } from '@klp/design/Theme/theme';
import type { ContractToRegistration } from '../../../classes';
import { CheckOffContractRequest } from '../../../classes/CheckOffContractRequest';
import type { RegistrationStatuses } from '../../../services/rtkApi';
import {
    Activities,
    useExecuteTaskMutation,
    useGetContractToRegistrationQuery,
    useRegisterContractMutation,
} from '../../../services/rtkApi';
import type { IContractToRegistrationProcessActivity } from '../../../classes/ContractToRegistrationProcessResponse';
import {
    ContractManagementRoutes,
    ContractManagementTabs,
} from '@forvalter/routing/Routes';
import {
    handleRequiredFormFields,
    useMapUserRolesToCandidateGroup,
} from '../../../utils';
import { FormContainerKontraktsforvaltning } from '@klp/formgenerator/index';
import { contractregistrationSelectors } from '@klp/contractregistration/contractToRegistrationSlice';

const useStyles = makeStyles(() => ({
    checkoff: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        paddingTop: '150 px',
    },
    D365: {
        maxWidth: '490px',
    },
    dokument: {
        width: '800px',
        marginTop: '10px',
        paddingTop: '2px',
        paddingBottom: '2px',
        paddingLeft: '20px',
        borderColor: theme.palette.primary.medium,
        borderRadius: 2,
        border: '1px solid transparent',
    },
    buttons: {
        width: '120px',
        paddingTop: '10px',
        paddingBottom: '10px',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    placeholder: {
        border: '1px solid transparent',
        borderColor: theme.palette.primary.medium,
        borderRadius: 2,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        display: 'flex',
        minWidth: '16rem',
        alignItems: 'center',
        '& > *': {
            display: 'flex',
            '&:first-child': {
                flex: '1 1 auto',
            },
            '&:last-child': {
                cursor: 'pointer',
                '&:hover svg': {
                    fill: theme.palette.secondary.main,
                },
            },
        },
    },
    rectangle: {
        color: 'red',
        background: KlpPalette.secondary.light,
        width: '70px',
        marginTop: '30px',
        height: '220px',
    },
    button: {
        textTransform: 'none',
        width: '100px',
    },
    noWrap: {
        whiteSpace: 'nowrap',
    },
    buttonWrapper: {
        width: 'max-content',
        cursor: 'not-allowed',
    },
}));

type Activity =
    | Activities.VURDER_RELEVANS
    | Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT;

type LinkDocumentAndLineStepProps = {
    handleCompleteStep: (step?: number) => void;
    status: RegistrationStatuses.FULLFORT;
    activity: {
        [key in Activity]: IContractToRegistrationProcessActivity;
    };
    prosessInstansId: string;
};

export default function LinkDocumentAndLineStep({
    handleCompleteStep,
    activity,
    status,
    prosessInstansId,
}: LinkDocumentAndLineStepProps) {
    const { t } = useTranslation();
    const anchorRef = useRef<HTMLButtonElement>(null);
    const { formFieldProcessing, formFieldError } = useSelector(
        contractregistrationSelectors,
    );
    const classes = useStyles();
    const history = useHistory();
    const { kontraktId } = useParams<{ kontraktId: string }>();
    const [rows, setRows] = useState<Array<number>>([]);

    const SQUARE_METER_SYMBOL = '\u33A1'; // ㎡

    const {
        data: contractToRegistration,
        isLoading: contractToRegistrationLoading,
        isFetching: contractToRegistrationFetching,
    } = useGetContractToRegistrationQuery({
        kontraktId,
    });
    const [
        registerContract,
        { isLoading: registerContractLoading, isError: registerContractError },
    ] = useRegisterContractMutation();

    const [
        executeTask,
        { isLoading: executeTaskLoading, isError: executeTaskError },
    ] = useExecuteTaskMutation();

    const { isUserCandidateForActivity } = useMapUserRolesToCandidateGroup(
        activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT],
    );

    const formFieldIsRequired = handleRequiredFormFields(
        activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]?.formFields,
    );

    const kontraktLinjer = contractToRegistration?.getLinjer() ?? [];

    const isLoading =
        registerContractLoading ||
        contractToRegistrationFetching ||
        executeTaskLoading ||
        formFieldProcessing;

    const baseIsDisabled =
        activity?.[Activities.VURDER_RELEVANS]?.utfortDato === null ||
        activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]?.id === null ||
        activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]?.utfortDato !==
            null ||
        (activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]?.id !== null &&
            activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]
                ?.utfortDato !== null) ||
        executeTaskLoading ||
        formFieldProcessing ||
        !isUserCandidateForActivity;

    const isDisabled =
        activity?.[Activities.VURDER_RELEVANS]?.utfortDato === null ||
        activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]?.id === null ||
        activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]?.utfortDato !==
            null ||
        (activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]?.id !== null &&
            activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]
                ?.utfortDato !== null) ||
        executeTaskLoading ||
        formFieldProcessing ||
        !isUserCandidateForActivity ||
        formFieldError ||
        formFieldIsRequired;

    const isDisabledWithoutFormError = baseIsDisabled;

    const handleRouting = () => {
        history.push(
            ContractManagementRoutes.tab(ContractManagementTabs.registrering),
        );

        showToast({
            body: t('contractRegistration.successfulRegistration'),
            type: 'success',
        });
    };

    const handleComplete = () => {
        const taskId =
            activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]?.id;

        if (handleCompleteStep) {
            handleCompleteStep();
        }

        if (!taskId || !status) {
            return;
        }

        executeTask({
            oppgaveId: taskId,
            registreringStatus: status,
        });

        handleRouting();
    };

    const handleSaveAvsjekk = async (
        contract: ContractToRegistration,
        contractRows: Array<number>,
    ) => {
        const kontraktLinjeIds: Array<string> = [];
        const currentKontraktLinjeIds = contract?.getLinjer();

        if (currentKontraktLinjeIds) {
            contractRows.forEach((element) => {
                kontraktLinjeIds.push(currentKontraktLinjeIds[element].id);
            });
        }

        if (!contract || !kontraktLinjeIds) {
            return;
        }

        await registerContract(
            new CheckOffContractRequest({
                dokumentId: contract.dokumentId,
                kontraktId: contract.kontrakter?.[0]?.kontraktNummer,
                kontraktlinjeIds: kontraktLinjeIds,
                kommentar: '',
                skalRegistreres: contract.skalRegistreres,
            }),
        );
        handleComplete();
    };

    const onRowSelectionChange = (
        _currentRowsSelected: Array<{ dataIndex: number; index: number }>,
        _allRowsSelected: Array<{ dataIndex: number; index: number }>,
        rowsSelected: Array<number>,
    ) => {
        setRows(rowsSelected);
    };

    useEffect(() => {
        const currentKontraktLinjeIds: Array<number> = [];
        contractToRegistration?.getLinjer().forEach((element, index) => {
            if (
                contractToRegistration?.avsjekkKontraktlinjeIds.includes(
                    element.id,
                )
            ) {
                currentKontraktLinjeIds.push(index);
            }
            setRows(currentKontraktLinjeIds);
        });
    }, [
        contractToRegistration,
        contractToRegistrationLoading,
        contractToRegistrationFetching,
    ]);

    if (executeTaskError || registerContractError) {
        showToast({
            body: t('contractRegistration.error'),
            type: 'error',
        });
    }

    const tableColumns: Array<MUIDataTableColumn> = [
        {
            label: t('checkOffContract.table.kontraktId'),
            name: 'kontraktId',
            options: {
                customBodyRenderLite: (dataIndex) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {kontraktLinjer[dataIndex]?.kontraktId}
                    </Typography>
                ),
            },
        },
        {
            label: t('checkOffContract.table.name'),
            name: 'navn',
            options: {
                customBodyRender: (value) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {value}
                    </Typography>
                ),
            },
        },
        {
            label: t('checkOffContract.table.areal'),
            name: 'arealAntall',
            options: {
                filter: false,
                customBodyRenderLite: (dataIndex) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {kontraktLinjer[dataIndex]?.areal > 0 ? (
                            <>
                                <Amount
                                    value={kontraktLinjer[dataIndex]?.areal}
                                />{' '}
                                {SQUARE_METER_SYMBOL}
                            </>
                        ) : kontraktLinjer[dataIndex]?.antall > 0 ? (
                            <>
                                {kontraktLinjer[dataIndex]?.antall}{' '}
                                {t('contract.pcs')}
                            </>
                        ) : (
                            <></>
                        )}
                    </Typography>
                ),
            },
        },
        {
            label: t('checkOffContract.table.kontraktLinjeIds'),
            name: 'kontraktlinjeIds',
            options: {
                display: false,
                viewColumns: false,
            },
        },
        {
            label: t('checkOffContract.table.validFromDate'),
            name: 'gyldigFra',
            options: {
                customBodyRender: (value) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {formatDate(value)}
                    </Typography>
                ),
            },
        },
        {
            label: t('checkOffContract.table.validToDate'),
            name: 'gyldigTil',
            options: {
                customBodyRender: (value) => (
                    <Typography klpTypographyVariant="small" klpColor="main">
                        {formatDate(value)}
                    </Typography>
                ),
            },
        },
        {
            label: t('checkOffContract.table.yearlyPrice'),
            name: 'aarligBelop',
            options: {
                customBodyRender: (value) => (
                    <Typography
                        klpTypographyVariant="small"
                        className={classes.noWrap}
                        klpColor="main"
                    >
                        <Amount value={value} />
                        &#8239; NOK
                    </Typography>
                ),
            },
        },
    ];

    const tableOptions: MUIDataTableOptions = {
        onRowSelectionChange: onRowSelectionChange,
        search: false,
        download: false,
        print: false,
        viewColumns: false,
        filter: false,
        sort: true,
        sortOrder: {
            name: 'gyldigFra',
            direction: 'desc',
        },
        selectableRowsHeader: false,
        rowsSelected: rows,
        filterType: 'multiselect',
        customToolbarSelect: () => <></>,
        textLabels: {
            body: {
                noMatch: t('checkOffContract.table.noRelevantContractLines'),
                toolTip: t('tables.body.toolTip'),
                columnHeaderTooltip: (column) =>
                    t('tables.body.toolTipFor') + column.label,
            },
            pagination: {
                next: t('tables.pagination.next'),
                previous: t('tables.pagination.previous'),
                rowsPerPage: t('tables.pagination.rowsPerPage'),
                displayRows: t('tables.pagination.displayRows'),
            },
            filter: {
                all: t('tables.filter.all'),
                title: t('tables.filter.title'),
                reset: t('tables.filter.reset'),
            },
            toolbar: {
                search: t('tables.toolbar.search'),
                downloadCsv: t('tables.toolbar.downloadCsv'),
                print: t('tables.toolbar.print'),
                viewColumns: t('tables.toolbar.viewColumns'),
                filterTable: t('tables.toolbar.filterTable'),
            },
            viewColumns: {
                title: t('tables.viewColumns.title'),
                titleAria: t('tables.viewColumns.titleAria'),
            },
            selectedRows: {
                text: t('tables.selectedRows.text'),
                delete: t('tables.selectedRows.delete'),
                deleteAria: t('tables.selectedRows.deleteAria'),
            },
        },
        setFilterChipProps: () => {
            return {
                color: 'secondary',
                variant: 'outlined',
            };
        },
    };

    return (
        <>
            {contractToRegistration && (
                <div className={classes.checkoff}>
                    <Grid
                        container={true}
                        direction="column"
                        justifyContent="flex-start"
                    >
                        <Block paddingTop="fat" paddingLeft="compact">
                            <Heading
                                klpHeadingLevel="TWO"
                                klpColor="dark"
                                translation={`checkOffContract.step.${activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]?.taskDefinitionId}.secondaryTitle`}
                                translationOptions={{
                                    kundeNavn:
                                        contractToRegistration?.kundeNavn,
                                    kundeNummer:
                                        contractToRegistration?.kundeNummer,
                                }}
                                data-cy="CheckOffContract__Title"
                            />
                        </Block>
                        <>
                            <Block paddingTop="fat" paddingLeft="compact">
                                <Loader
                                    isLoading={contractToRegistrationLoading}
                                >
                                    <MUIDataTable
                                        title=""
                                        data={kontraktLinjer}
                                        columns={tableColumns}
                                        options={tableOptions}
                                    />
                                </Loader>
                            </Block>
                        </>
                    </Grid>

                    <Block paddingLeft="compact" paddingTop="fat">
                        {activity?.[Activities.TILKNYTT_DOKUMENT_TIL_KONTRAKT]
                            ?.formFields && (
                            <FormContainerKontraktsforvaltning
                                activity={
                                    activity?.[
                                        Activities
                                            .TILKNYTT_DOKUMENT_TIL_KONTRAKT
                                    ]
                                }
                                prosessInstansId={prosessInstansId}
                                isDisabled={isDisabledWithoutFormError}
                            />
                        )}
                    </Block>
                    <Block paddingLeft="compact" paddingTop="fat">
                        {isLoading ? (
                            <Loader isLoading={isLoading} />
                        ) : (
                            <>
                                <Block>
                                    {isDisabled ? (
                                        <Block>
                                            <Tooltip
                                                translation="checkOffContract.tooltip"
                                                title=""
                                            >
                                                <Box
                                                    className={
                                                        classes.buttonWrapper
                                                    }
                                                >
                                                    <ActionButton
                                                        disabled={isDisabled}
                                                        actionType="save"
                                                        translation="checkOffContract.saveButton"
                                                        onClick={() =>
                                                            handleSaveAvsjekk(
                                                                contractToRegistration,
                                                                rows,
                                                            )
                                                        }
                                                        klpTypographyVariant="small"
                                                    />
                                                </Box>
                                            </Tooltip>
                                        </Block>
                                    ) : (
                                        <>
                                            <ActionButton
                                                disabled={isLoading}
                                                forwardedRef={anchorRef}
                                                actionType="save"
                                                translation="checkOffContract.saveButton"
                                                onClick={handleComplete}
                                                klpTypographyVariant="small"
                                            />
                                        </>
                                    )}
                                </Block>
                            </>
                        )}
                    </Block>
                </div>
            )}
        </>
    );
}
